import type { ReactNode } from 'react';
import { Caption1 } from '@fluentui/react-components';
import type { Person, PersonChecklist } from '@vigeo/shared';
import { ChecklistBands, ReadinessSummary } from '@/features/readiness';
import { ControlledFormIssuer } from '../ControlledFormIssuer.js';
import { PersonFormsPanel } from '../PersonFormsPanel.js';
import { RequestsPanel } from '@/features/requests';
import { FilesPanel } from '@/features/files';
import { usePersonStyles } from './styles.js';

/**
 * STEP 4 — DOCUMENTS & CHECKLIST, trimmed to frame `3.1` (`323:1347`) and read in its order
 * (#462 §5 item 2, D14, inventory in #484).
 *
 * The frame draws two halves: the grouped list of the candidate's portal forms, then **HR's part —
 * checks and verification**. This file is now ordered that way. It was not: the list of forms sat
 * second, under a "Request a published form" fold-out, so the step's own subject opened below a
 * control for adding to it.
 *
 * SEVEN BLOCKS BECAME FIVE, and nothing left the app. That constraint is the whole reason this is a
 * trim rather than a removal — #484 enumerated it, and every block the frame does not draw turned
 * out to be either the only path to a feature or the only path for some lifecycle state:
 *
 *   - "Request a published form" and the one-off requests render NOWHERE else. There is no
 *     `/app/forms`-equivalent for issuing to a person and no requests route at all, so trimming
 *     them out would have made two working features unreachable. They are folded together instead:
 *     both are HR asking this person for something, and both gate on `people.issue-steps`.
 *   - Files has no per-person list anywhere else. Individual files open from History and Offer and
 *     stage through `StoredFileLink`; the list is only here. It sits under HR's part, because the
 *     frame's "checks and verification" is about evidence and the files ARE the evidence.
 *   - The readiness summary looked like a duplicate of step 1 and is not: step 1 renders it
 *     `compact` and only on its non-candidate branch (#428), so A CANDIDATE SEES IT ONLY HERE.
 *     Dropping it would have been a visible loss dressed up as a trim.
 *
 * Still owed, and listed on #484 rather than invented here: the frame's success message after Move
 * to Onboarding ("21 forms added, Welcome email sent"), and whether its "orientation and
 * competency" line is a summary of step 7 or content of its own (Paul).
 */
export interface DocumentsStepProps {
  readonly person: Person;
  readonly canEdit: boolean;
  readonly checklistNotice: ReactNode;
  readonly ready: PersonChecklist | undefined;
}

export function DocumentsStep({ person, canEdit, checklistNotice, ready }: DocumentsStepProps) {
  const styles = usePersonStyles();
  return <div className={styles.page}>
    {/*
      THE FRAME'S FIRST HALF, and so the first thing here.

      ADR-013 §4: HR can open any of the onboarding forms, and each opens with two tabs. `canEdit`
      does NOT gate this — reading a form is `review.read`, which a viewer holds, and the API omits
      the rows a caller may not read rather than trusting the client to hide them. Gating here would
      hide readable forms from the people whose job is reading them.
    */}
    <section className={styles.card} aria-labelledby="person-forms-heading">
      <h3 id="person-forms-heading" className={styles.h}>Forms sent to this person</h3>
      <Caption1>Open a form to see the answers so far and what the candidate sees.</Caption1>
      <PersonFormsPanel personId={person.id} candidateFirstName={person.name.preferredName || person.name.givenName} />
    </section>

    {/* THE FRAME'S SECOND HALF: HR's part — checks and verification. */}
    {checklistNotice}
    {ready ? <>
      <ReadinessSummary checklist={ready} />
      <section className={styles.card} aria-label="Checklist"><h3 className={styles.h}>Requirements</h3><Caption1>Open a group to see its requirements. Completed items remain available within each group.</Caption1><ChecklistBands checklist={ready} canRecord={canEdit} startCollapsed /></section>
    </> : null}

    {/* `files.read` is Admin-only (auth/policy.ts), so a viewer gets no Files heading at all —
        an empty section is worse than no section. The panel ALSO refuses to request a list it
        may not read (`useMayReadFiles`), because this page is not the only thing that renders
        a files reader: History and the Hiring section do too, through `StoredFileLink`, and
        gating only here left both of those asking (#270). This line is the heading; the
        capability is enforced in the feature. */}
    {canEdit ? <section className={styles.card} aria-labelledby="person-files-heading"><h3 id="person-files-heading" className={styles.h}>Files</h3><FilesPanel personId={person.id} /></section> : null}

    {/*
      ONE BLOCK FOR BOTH WAYS OF ASKING (#484). Two cards used to carry this: a "Request a published
      form" fold-out at the very top of the step, and one-off requests at the bottom. They are the
      same move from HR's side and the same capability, and the frame draws neither, so one block is
      strictly less surplus than two.

      THE TWO ARE STILL NAMED SEPARATELY INSIDE IT, because they do not behave alike: a published
      form carries `satisfies` and can close a requirement, while a one-off request deliberately
      counts toward nothing. One caption over both would have had to be wrong about one of them.
    */}
    <section className={styles.card} aria-labelledby="ask-for-documents-heading">
      <h3 id="ask-for-documents-heading" className={styles.h}>Ask this person for something</h3>
      <div>
        <Caption1>A published form goes to their portal and can close a requirement on the checklist above.</Caption1>
        <ControlledFormIssuer personId={person.id} canEdit={canEdit} />
      </div>
      <div>
        <Caption1>A one-off request is a document asked of this person alone. It is tracked here and counts toward nothing.</Caption1>
        <RequestsPanel personId={person.id} canEdit={canEdit} />
      </div>
    </section>
  </div>;
}
